import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'
import {
    Calendar,
    CheckCircle,
    XCircle,
    TrendingUp,
    ArrowRight,
    Loader2,
    Filter,
    X,
    ChevronDown,
    ChevronUp
} from 'lucide-react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts'
import { format } from 'date-fns'
import { fr, enUS } from 'date-fns/locale'
import DatePicker, { registerLocale } from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import './DashboardPage.css'

registerLocale('fr', fr)

export default function DashboardPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { user, hasPermission } = useAuthStore()
    const [stats, setStats] = useState(null)
    const [evolution, setEvolution] = useState([])
    const [recentRdv, setRecentRdv] = useState([])
    const [selectedStatus, setSelectedStatus] = useState('all')
    const [loading, setLoading] = useState(true)
    const [showFilters, setShowFilters] = useState(false)
    const [agents, setAgents] = useState([])
    const [filters, setFilters] = useState({
        period: 'today',
        dateRange: { start: '', end: '' },
        status: 'all',
        agent_id: ''
    })
    // Initial fetch with default filters (Today)
    const [activeFilters, setActiveFilters] = useState({
        period: 'today',
        dateRange: { start: '', end: '' },
        status: 'all',
        agent_id: ''
    })

    const dateLocale = i18n.language === 'fr' ? fr : enUS

    useEffect(() => {
        if (hasPermission('DASHBOARD_FILTER_AGENT')) {
            fetchAgents()
        }
        fetchDashboardData()
    }, [activeFilters])

    const fetchAgents = async () => {
        try {
            const res = await api.get('/users/agents')
            setAgents(res.data.data.users)
        } catch (error) {
            console.error('Error fetching agents:', error)
        }
    }

    // Update draft filters when period changes to set default dates if needed
    // (Optional: keep as is, just let user select)

    const handleApplyFilters = () => {
        setActiveFilters(filters)
    }

    const getDisplayDateRange = () => {
        const { period, dateRange } = activeFilters
        let start = new Date()
        let end = new Date()

        if (period === 'custom' && dateRange.start && dateRange.end) {
            start = new Date(dateRange.start)
            end = new Date(dateRange.end)
        } else {
            start.setHours(0, 0, 0, 0)
            end.setHours(23, 59, 59, 999)

            switch (period) {
                case 'today':
                    break
                case 'thisWeek':
                    const day = start.getDay()
                    const diff = start.getDate() - day + (day === 0 ? -6 : 1)
                    start.setDate(diff)
                    break
                case 'thisMonth':
                    start.setDate(1)
                    break
                case '30d':
                    start.setDate(start.getDate() - 30)
                    break
                case '90d':
                    start.setDate(start.getDate() - 90)
                    break
                case '7d':
                default:
                    start.setDate(start.getDate() - 6)
            }
        }
        return { start, end }
    }

    const { start: displayStart, end: displayEnd } = getDisplayDateRange()

    const fetchDashboardData = async () => {
        setLoading(true)
        try {
            const { period, dateRange, status, agent_id } = activeFilters
            const params = { period }

            if (period === 'custom' && dateRange.start && dateRange.end) {
                params.startDate = dateRange.start
                params.endDate = dateRange.end
            }

            if (status !== 'all') {
                params.etat = status
            }

            if (agent_id) {
                params.agent_id = agent_id
            }

            const [statsRes, evolutionRes, recentRes] = await Promise.all([
                api.get('/dashboard/stats', { params }), // Stats needs date filter
                api.get('/dashboard/evolution', { params }), // Chart needs all filters
                api.get('/dashboard/recent', { params }) // Recent needs status filter
            ])

            setStats(statsRes.data.data.stats)
            setEvolution(evolutionRes.data.data.evolution)
            setRecentRdv(recentRes.data.data.rendezvous)
        } catch (error) {
            console.error('Error fetching dashboard data:', error)
        } finally {
            setLoading(false)
        }
    }

    const getStateColor = (state) => {
        const colors = {
            PLANIFIE: 'var(--info-500)',
            QUALITE_DONE: 'var(--primary-500)',
            DISPATCHED: 'var(--warning-500)',
            CONSEILLER_DONE: 'var(--success-500)',
            ANNULE: 'var(--error-500)'
        }
        return colors[state] || 'var(--text-muted)'
    }

    const getStateBadgeClass = (state) => {
        const classes = {
            PLANIFIE: 'badge-info',
            QUALITE_DONE: 'badge-primary',
            DISPATCHED: 'badge-warning',
            CONSEILLER_DONE: 'badge-success',
            ANNULE: 'badge-error'
        }
        return classes[state] || 'badge-neutral'
    }

    if (loading) {
        return (
            <div className="dashboard-loading">
                <Loader2 size={48} className="spin" />
                <p>{t('common.loading')}</p>
            </div>
        )
    }

    return (
        <div className="dashboard-page">
            {/* Page Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('dashboard.title')}</h1>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span>
                            {t('dashboard.periodDisplay')}: <strong>{format(displayStart, 'Pp', { locale: dateLocale })} - {format(displayEnd, 'Pp', { locale: dateLocale })}</strong>
                        </span>
                        {(hasPermission('DASHBOARD_FILTER_DATE') || hasPermission('DASHBOARD_FILTER_STATUS') || hasPermission('DASHBOARD_FILTER_AGENT')) && (
                            <button
                                className={`btn btn-sm ${showFilters ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <Filter size={16} />
                                {showFilters ? t('common.hideFilters') : t('common.showFilters')}
                            </button>
                        )}
                    </div>
                </div>

                {hasPermission('ADD_RDV') && (
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/rendezvous/add')}
                    >
                        <Calendar size={18} />
                        {t('rendezvous.add')}
                    </button>
                )}
            </div>

            {/* Filters Modal */}
            {showFilters && (
                <div className="dashboard-modal-overlay">
                    <div className="dashboard-modal">
                        <div className="dashboard-modal-header">
                            <div className="dashboard-modal-title">
                                <Filter size={24} className="text-primary-600" />
                                <span>{t('common.filter')}</span>
                            </div>
                            <button
                                onClick={() => setShowFilters(false)}
                                className="btn btn-ghost btn-square rounded-full"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="dashboard-modal-body">
                            {/* Period Selection */}
                            {hasPermission('DASHBOARD_FILTER_DATE') && (
                                <div className="modal-form-group">
                                    <label className="modal-label">{t('common.dateRange')}</label>
                                    <select
                                        className="modal-select"
                                        value={filters.period}
                                        onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
                                    >
                                        <option value="today">{t('dashboard.period.today')}</option>
                                        <option value="thisWeek">{t('dashboard.period.thisWeek')}</option>
                                        <option value="thisMonth">{t('dashboard.period.thisMonth')}</option>
                                        <option value="7d">{t('dashboard.period.7d')}</option>
                                        <option value="30d">{t('dashboard.period.30d')}</option>
                                        <option value="90d">{t('dashboard.period.90d')}</option>
                                        <option value="custom">{t('dashboard.period.custom')}</option>
                                    </select>

                                    {filters.period === 'custom' && (
                                        <div className="grid grid-cols-2 gap-4 mt-2">
                                            <div className="modal-form-group">
                                                <label className="text-xs font-semibold mb-1 block">{t('common.from')}</label>
                                                <div className="modal-date-picker-container">
                                                    <DatePicker
                                                        selected={filters.dateRange.start ? new Date(filters.dateRange.start) : null}
                                                        onChange={(date) => setFilters(prev => ({
                                                            ...prev,
                                                            dateRange: { ...prev.dateRange, start: date ? date.toISOString() : '' }
                                                        }))}
                                                        showTimeSelect
                                                        timeFormat="HH:mm"
                                                        timeIntervals={15}
                                                        dateFormat="Pp"
                                                        locale={i18n.language === 'fr' ? 'fr' : 'en-US'}
                                                        placeholderText="JJ/MM/AAAA HH:mm"
                                                    />
                                                </div>
                                            </div>
                                            <div className="modal-form-group">
                                                <label className="text-xs font-semibold mb-1 block">{t('common.to')}</label>
                                                <div className="modal-date-picker-container">
                                                    <DatePicker
                                                        selected={filters.dateRange.end ? new Date(filters.dateRange.end) : null}
                                                        onChange={(date) => setFilters(prev => ({
                                                            ...prev,
                                                            dateRange: { ...prev.dateRange, end: date ? date.toISOString() : '' }
                                                        }))}
                                                        showTimeSelect
                                                        timeFormat="HH:mm"
                                                        timeIntervals={15}
                                                        dateFormat="Pp"
                                                        locale={i18n.language === 'fr' ? 'fr' : 'en-US'}
                                                        placeholderText="JJ/MM/AAAA HH:mm"
                                                        minDate={filters.dateRange.start ? new Date(filters.dateRange.start) : null}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Status Filter */}
                            {hasPermission('DASHBOARD_FILTER_STATUS') && (
                                <div className="modal-form-group">
                                    <label className="modal-label">{t('rendezvous.etat')}</label>
                                    <select
                                        className="modal-select"
                                        value={filters.status}
                                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    >
                                        <option value="all">{t('common.all')}</option>
                                        <option value="PLANIFIE">{t('rendezvous.states.PLANIFIE')}</option>
                                        <option value="SIGNEE">{t('rendezvous.states.SIGNEE')}</option>
                                        <option value="ANNULE">{t('rendezvous.states.ANNULE')}</option>
                                        <option value="ERREUR">{t('rendezvous.states.ERREUR')}</option>
                                        <option value="QUALITE_DONE">{t('rendezvous.states.QUALITE_DONE')}</option>
                                        <option value="CONSEILLER_DONE">{t('rendezvous.states.CONSEILLER_DONE')}</option>
                                        <option value="DISPATCHED">{t('rendezvous.states.DISPATCHED')}</option>
                                    </select>
                                </div>
                            )}

                            {/* Agent Filter */}
                            {hasPermission('DASHBOARD_FILTER_AGENT') && (
                                <div className="modal-form-group">
                                    <label className="modal-label">{t('rendezvous.agent')}</label>
                                    <select
                                        className="modal-select"
                                        value={filters.agent_id || ''}
                                        onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                                    >
                                        <option value="">{t('common.all')}</option>
                                        {agents.map(agent => (
                                            <option key={agent._id} value={agent._id}>
                                                {agent.prenom} {agent.nom}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="dashboard-modal-footer">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setFilters(activeFilters); // Revert
                                    setShowFilters(false);
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    handleApplyFilters();
                                    setShowFilters(false);
                                }}
                            >
                                <Filter size={18} />
                                {t('common.filter')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="dashboard-stats">
                {hasPermission('DASHBOARD_TOTAL_RDV') && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--info-500), var(--info-600))' }}>
                            <Calendar size={24} color="white" />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats?.totalRdv || 0}</span>
                            <span className="stat-label">{t('dashboard.totalRdv')}</span>
                        </div>
                    </div>
                )}

                {hasPermission('DASHBOARD_TOTAL_RDV_SIGNE') && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--success-500), var(--success-600))' }}>
                            <CheckCircle size={24} color="white" />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats?.totalSigne || 0}</span>
                            <span className="stat-label">{t('dashboard.totalSigne')}</span>
                        </div>
                    </div>
                )}

                {hasPermission('DASHBOARD_TOTAL_RDV_ANNULE') && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--error-500), var(--error-600))' }}>
                            <XCircle size={24} color="white" />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats?.totalAnnule || 0}</span>
                            <span className="stat-label">{t('dashboard.totalAnnule')}</span>
                        </div>
                    </div>
                )}

                {hasPermission('DASHBOARD_TOTAL_RDV_SIGNE') && (
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--primary-500), var(--primary-600))' }}>
                            <TrendingUp size={24} color="white" />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">
                                {stats?.totalRdv > 0 && stats?.totalSigne >= 0
                                    ? `${Math.round((stats.totalSigne / stats.totalRdv) * 100)}%`
                                    : '0%'}
                            </span>
                            <span className="stat-label">{t('dashboard.conversionRate')}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Evolution Chart */}
            {hasPermission('DASHBOARD_EVOLUTION_RDV') && (
                <div className="card dashboard-chart">
                    <div className="card-header">
                        <h3>{t('dashboard.evolution')}</h3>
                        <div className="card-header">
                            <h3>{t('dashboard.evolution')}</h3>
                            {/* Period selection moved to global filter */}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={evolution}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(99, 102, 241, 0.1)" />
                                    <XAxis
                                        dataKey="date"
                                        stroke="var(--text-muted)"
                                        fontSize={12}
                                        tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: dateLocale })}
                                    />
                                    <YAxis stroke="var(--text-muted)" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '8px',
                                            color: 'var(--text-primary)'
                                        }}
                                        labelFormatter={(value) => format(new Date(value), 'PPP', { locale: dateLocale })}
                                    />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="total"
                                        stroke="var(--primary-500)"
                                        strokeWidth={2}
                                        dot={false}
                                        name="Total"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="signe"
                                        stroke="var(--success-500)"
                                        strokeWidth={2}
                                        dot={false}
                                        name="Signés"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="annule"
                                        stroke="var(--error-500)"
                                        strokeWidth={2}
                                        dot={false}
                                        name="Annulés"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}


            {/* Agent Stats Table */}
            {hasPermission('DASHBOARD_STATS_AGENT') && stats?.byAgent && (
                <div className="card dashboard-agent-stats mb-6">
                    <div className="card-header">
                        <h3>{t('dashboard.statsByAgent')}</h3>
                    </div>
                    <div className="table-container">
                        <table className="table agent-stats-table">
                            <thead>
                                <tr>
                                    <th>{t('rendezvous.agent')}</th>
                                    <th>{t('dashboard.totalRdv')}</th>
                                    <th>{t('rendezvous.states.PLANIFIE')}</th>
                                    <th>{t('dashboard.table.qualifiedDispatched')}</th>
                                    <th>{t('rendezvous.states.SIGNEE')}</th>
                                    <th>{t('rendezvous.states.ERREUR')}</th>
                                    <th>{t('rendezvous.states.ANNULE')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.byAgent.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted p-4">
                                            {t('common.noData')}
                                        </td>
                                    </tr>
                                ) : (
                                    stats.byAgent.map((agentStat) => (
                                        <tr key={agentStat._id?._id || 'unknown'}>
                                            <td className="font-medium">
                                                {agentStat._id ? `${agentStat._id.prenom} ${agentStat._id.nom}` : t('common.unknown')}
                                            </td>
                                            <td><span className="badge badge-neutral">{agentStat.count}</span></td>
                                            <td><span className="text-info">{agentStat.planifie || 0}</span></td>
                                            <td><span className="text-primary">{agentStat.qualifie || 0}</span></td>
                                            <td><span className="text-success">{agentStat.signe || 0}</span></td>
                                            <td><span className="text-warning">{agentStat.erreur || 0}</span></td>
                                            <td><span className="text-error">{agentStat.annule || 0}</span></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Recent Appointments */}
            <div className="card dashboard-recent">
                <div className="card-header">
                    <h3>{t('dashboard.recentRdv')}</h3>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/rendezvous')}
                    >
                        {t('common.view')} <ArrowRight size={16} />
                    </button>
                </div>
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{t('rendezvous.numero')}</th>
                                <th>{t('rendezvous.client')}</th>
                                <th>{t('rendezvous.phone')}</th>
                                <th>{t('rendezvous.date')}</th>
                                <th>{t('rendezvous.agent')}</th>
                                {hasPermission('DASHBOARD_FILTER_STATUS') && <th>{t('rendezvous.etat')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {recentRdv.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted p-6">
                                        {t('common.noData')}
                                    </td>
                                </tr>
                            ) : (
                                recentRdv.map((rdv) => (
                                    <tr
                                        key={rdv._id}
                                        onClick={() => navigate(`/rendezvous/${rdv._id}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>#{rdv.numeroSequentiel}</td>
                                        <td>
                                            {rdv.client_rdv?.prenom_client} {rdv.client_rdv?.nom_client}
                                        </td>
                                        <td>{rdv.client_rdv?.natel_client}</td>
                                        <td>
                                            {format(new Date(rdv.dateHeure_rdv), 'Pp', { locale: dateLocale })}
                                        </td>
                                        <td>
                                            {rdv.agent_rdv?.prenom} {rdv.agent_rdv?.nom}
                                        </td>
                                        {hasPermission('DASHBOARD_FILTER_STATUS') && (
                                            <td>
                                                <span className={`badge ${getStateBadgeClass(rdv.etat_rdv)}`}>
                                                    {t(`rendezvous.states.${rdv.etat_rdv}`)}
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
